class HvProductPrice extends HTMLElement {
  connectedCallback() {
    this.bound = false;
    this.hoverPlan = null;
    this.pillCleanups = [];
    this.onVariantChange = this.onVariantChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.setup();
  }

  disconnectedCallback() {
    this.teardown();
  }

  formatMoney(cents) {
    var fmt = this.dataset.moneyFormat || window.theme?.settings?.money_with_currency_format;
    if (typeof formatMoney === 'function') return formatMoney(cents, fmt);
    return (Number(cents) / 100).toFixed(2) + ' €';
  }

  clampPlan(n) {
    var x = parseInt(String(n), 10);
    return (x === 2 || x === 3 || x === 4) ? x : 3;
  }

  /** Update label, amount and pill highlights. commit=true saves the active plan to dataset. */
  renderPlan(plan, commit) {
    var p = this.clampPlan(plan);
    if (commit) this.dataset.activePlan = String(p);
    var cents = Number(this.dataset.priceCents);
    if (!cents) return;
    var each = Math.round(cents / p);
    this.querySelectorAll('.hv-installments__pill').forEach(function (btn) {
      var active = btn.getAttribute('data-plan') === String(p);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    var labelEl = this.querySelector('.hv-installments__plan-label');
    var amountEl = this.querySelector('.hv-installments__amount');
    if (labelEl) labelEl.textContent = p + ' x ';
    if (amountEl) amountEl.textContent = this.formatMoney(each);
  }

  /** Show hover preview without committing the active plan. */
  previewPlan(plan) {
    var p = this.clampPlan(plan);
    if (this.hoverPlan === p) return;
    this.hoverPlan = p;
    this.renderPlan(p, false);
  }

  /** Restore the last committed plan after hover ends. */
  clearPreview() {
    this.hoverPlan = null;
    this.renderPlan(parseInt(this.dataset.activePlan || '3', 10), false);
  }

  /** Bind mouseenter/mouseleave listeners on each plan pill. */
  bindPillHover() {
    this.unbindPillHover();
    var pillsEl = this.querySelector('.hv-installments__pills');
    if (!pillsEl) return;
    var self = this;
    pillsEl.querySelectorAll('.hv-installments__pill').forEach(function (pill) {
      var enter = function () {
        self.previewPlan(parseInt(pill.getAttribute('data-plan'), 10));
      };
      var leave = function (e) {
        if (e.relatedTarget && pillsEl.contains(e.relatedTarget)) return;
        self.clearPreview();
      };
      pill.addEventListener('mouseenter', enter);
      pill.addEventListener('mouseleave', leave);
      self.pillCleanups.push(function () {
        pill.removeEventListener('mouseenter', enter);
        pill.removeEventListener('mouseleave', leave);
      });
    });
  }

  unbindPillHover() {
    this.pillCleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
    this.pillCleanups = [];
  }

  syncAlmaInstallmentsVisibility() {
    var stack = this.querySelector('.hv-product-price-host__installments-stack');
    var summary = this.querySelector('.hv-product-price-host__installments-summary');
    if (!stack && !summary) return;
    var minCents = parseInt(this.dataset.almaMinimumCents, 10);
    var hide = this.classList.contains('hv-product-price-host--unavailable') || Number(this.dataset.priceCents) < minCents;
    if (stack) stack.classList.toggle('hv-installments--hidden', hide);
    if (summary) summary.classList.toggle('hv-installments--hidden', hide);
  }

  findAlmaBadge() {
    var sid = this.dataset.section;
    var section = this.closest('.shopify-section') || (sid && document.getElementById('shopify-section-' + sid));
    return (section && section.querySelector('#alma-badge')) || document.getElementById('alma-badge');
  }

  /** Open the Alma modal then select the Nx plan button inside it. */
  triggerAlmaPlan(plan) {
    var badge = this.findAlmaBadge();
    if (!badge) return;
    var planNumber = this.clampPlan(plan);
    var planLabel = planNumber + 'x';                                    // e.g. "3x"
    var planRegex = new RegExp('^' + planNumber + '\\s*x\\s*$', 'i');   // matches "3x", "3 x", etc.
    var modalTrigger = badge.querySelector('[data-testid="widget-button"]') || badge.querySelector('.alma-payment-plans-container');

    /**
     * Click the Nx plan button inside a container.
     *
     * Pass 1 — interactive elements whose full text equals "Nx"
     *   Works for <button>3x</button>.
     *
     * Pass 2 — leaf nodes whose text equals "Nx" → climb to interactive ancestor
     *   Required when buttons have children: <button><span>3x</span><span>16,67 €</span></button>
     *   button.textContent = "3x 16,67 €" fails pass 1; the leaf <span>3x</span> succeeds here.
     */
    function clickPlanIn(container) {
      if (!container) return false;

      // Pass 1: look for interactive elements whose full label is exactly "Nx"
      var interactiveNodes = container.querySelectorAll('button, [role="button"], [role="tab"], [role="radio"], label');
      for (var i = 0; i < interactiveNodes.length; i++) {
        var fullText = (interactiveNodes[i].textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (fullText === planLabel || planRegex.test(fullText)) {
          interactiveNodes[i].click();
          return true;
        }
      }

      // Pass 2: find leaf nodes with "Nx" text, then click their nearest interactive ancestor
      var allNodes = container.getElementsByTagName('*');
      for (var j = 0; j < allNodes.length; j++) {
        var node = allNodes[j];
        if (node.children && node.children.length) continue; // skip non-leaf nodes
        var leafText = (node.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
        if (leafText !== planLabel && !planRegex.test(leafText)) continue;
        var interactiveAncestor = node.closest('button, [role="button"], [role="tab"], [role="radio"], label, a')
          || (node.parentElement !== container ? node.parentElement : null);
        if (interactiveAncestor && interactiveAncestor !== container) {
          interactiveAncestor.click();
          return true;
        }
      }
      return false;
    }

    // Open the modal first
    if (modalTrigger) modalTrigger.click();

    const observer = new MutationObserver(() => {
      const activeOption = document.querySelector('.alma-eligibility-modal-active-option');
      if (!activeOption) return;
    
      observer.disconnect();
    
      const planButtonsContainer = activeOption.parentElement;
      if (planButtonsContainer && clickPlanIn(planButtonsContainer)) return;
    
      const eligibilityLine = document.querySelector('.alma-payment-plans-eligibility-line');
      clickPlanIn(eligibilityLine || badge);
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
  }

  /** Update price and installment display when the active variant changes. */
  updateVariant(v) {
    var ins = this.querySelector('.hv-product-price ins .amount');
    if (ins) ins.textContent = this.formatMoney(v.price);
    var del = this.querySelector('.hv-product-price del');
    var delAmt = this.querySelector('.hv-product-price del .amount');
    if (v.compare_at_price != null && v.compare_at_price > v.price) {
      if (delAmt) delAmt.textContent = this.formatMoney(v.compare_at_price);
      if (del) del.style.display = '';
    } else if (del) {
      del.style.display = 'none';
    }
    this.dataset.priceCents = String(v.price);
    this.renderPlan(parseInt(this.dataset.activePlan || '3', 10), true);
  }

  onVariantChange(e) {
    if (!e.detail || String(e.detail.sectionId) !== String(this.dataset.section)) return;
    var variant = e.detail.variant;
    if (!variant || !variant.available || !variant.price) {
      this.classList.add('hv-product-price-host--unavailable');
      this.syncAlmaInstallmentsVisibility();
      return;
    }
    this.classList.remove('hv-product-price-host--unavailable');
    this.updateVariant(variant);
    this.syncAlmaInstallmentsVisibility();
  }

  onClick(e) {
    var pill = e.target.closest('.hv-installments__pill');
    if (pill && this.contains(pill)) {
      e.preventDefault();
      this.hoverPlan = null;
      var plan = parseInt(pill.getAttribute('data-plan'), 10);
      this.renderPlan(plan, true);
      this.triggerAlmaPlan(plan);
      return;
    }
    var brand = e.target.closest('.hv-installments__brand');
    if (brand && this.contains(brand)) {
      e.preventDefault();
      this.triggerAlmaPlan(parseInt(this.dataset.activePlan || '3', 10));
    }
  }

  setup() {
    if (this.bound) return;
    this.bound = true;
    this.installmentsPills = this.querySelector('.hv-installments__pills');
    if (!this.dataset.activePlan) this.dataset.activePlan = '3';
    this.renderPlan(parseInt(this.dataset.activePlan, 10), true);
    this.syncAlmaInstallmentsVisibility();
    document.addEventListener('product:variant-change', this.onVariantChange);
    this.addEventListener('click', this.onClick);
    var self = this;
    if (this.installmentsPills) {
      this.bindPillHover();
    } else {
      this.pillObserver = new MutationObserver(() => {
        if (this.installmentsPills) {
          this.pillObserver.disconnect();
          this.pillObserver = null;
          this.bindPillHover();
        }
      });
      this.pillObserver.observe(this, { childList: true, subtree: true });
    }
  }

  teardown() {
    if (!this.bound) return;
    this.bound = false;
    if (this.pillObserver) {
      this.pillObserver.disconnect();
      this.pillObserver = null;
    }
    document.removeEventListener('product:variant-change', this.onVariantChange);
    this.removeEventListener('click', this.onClick);
    this.unbindPillHover();
  }
}

if (!customElements.get('hv-product-price')) {
  customElements.define('hv-product-price', HvProductPrice);
}
